import * as THREE from "three";
import type { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { PlanetAudio } from "./audio";
import { CameraRig } from "./camera-rig";
import { LANDMARKS, NPCS, landmarkById, type LandmarkId, type NpcId } from "./catalog";
import { createNpc, createLandmark } from "./figures";
import { Input, preferVirtualJoystick } from "./input";
import { createAmbientParticles, createClouds, createLights, createPlanet, createRenderer } from "./planet";
import { Player } from "./player";
import { loadSave, unlockBadge, unlockPostcard, type SaveState } from "./save";
import {
  INTERACT_RADIUS,
  LANDMARK_RADIUS,
  NPC_SPEED,
  PLANET_RADIUS,
  PLAYER_SPEED,
  greatCircleStep,
  latLonToVector3,
  moveOnSphere,
  orientOnSphere,
  surfaceDistance,
  tangentBasis,
} from "./sphere";
import { GameUI } from "./ui";

export class Game {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly labels: CSS2DRenderer;
  private readonly clock = new THREE.Clock();
  private readonly input: Input;
  private readonly ui: GameUI;
  private readonly audio = new PlanetAudio();
  private readonly player: Player;
  private readonly rig: CameraRig;
  private readonly clouds: THREE.Group;
  private readonly landmarks = new Map<LandmarkId, THREE.Group>();
  private readonly npcs = new Map<NpcId, THREE.Group>();
  private readonly guiding = new Set<NpcId>();
  private readonly save: SaveState;
  private readonly fwd = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly moveDir = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private started = false;
  private introYaw = 0.35;
  private readonly reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private raf = 0;
  private nearbyNpc: NpcId | null = null;
  private nearbyLandmark: LandmarkId | null = null;
  private readonly spawn = latLonToVector3(6, -150, PLANET_RADIUS);
  private walkTarget: THREE.Vector3 | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private planetMesh: THREE.Object3D | null = null;
  private ambientTick: (t: number, dt: number) => void = () => {};
  private nearLabel: HTMLElement | null = null;

  constructor(private readonly viewport: HTMLElement) {
    this.save = loadSave();
    this.ui = new GameUI(this.save);
    this.audio.setMuted(this.save.muted);
    const { renderer, labels } = createRenderer(viewport);
    this.renderer = renderer;
    this.labels = labels;
    this.camera = new THREE.PerspectiveCamera(48, 1, 0.1, 200);
    this.rig = new CameraRig(this.camera);
    this.input = new Input(
      renderer.domElement,
      document.getElementById("joystick") as HTMLElement,
      document.getElementById("joystick-knob") as HTMLElement,
    );
    this.input.showJoystick(preferVirtualJoystick());

    createLights(this.scene);
    const planet = createPlanet();
    this.planetMesh = planet.children[0] ?? null;
    this.scene.add(planet);
    this.clouds = createClouds();
    this.scene.add(this.clouds);
    const ambient = createAmbientParticles();
    this.scene.add(ambient.points);
    this.ambientTick = ambient.tick;

    for (const def of LANDMARKS) {
      const mesh = createLandmark(def);
      this.landmarks.set(def.id, mesh);
      this.scene.add(mesh);
    }
    for (const def of NPCS) {
      const mesh = createNpc(def);
      this.npcs.set(def.id, mesh);
      this.scene.add(mesh);
    }

    this.player = new Player();
    this.scene.add(this.player.group);

    this.ui.onEnter = () => void this.enter();
    this.ui.onTalk = (id) => this.ui.openTalk(id);
    this.ui.onGuide = (id) => this.guiding.add(id);
    this.ui.onMute = (muted) => this.audio.setMuted(muted);
    this.ui.onOpenSite = (url) => window.open(url, "_blank", "noopener,noreferrer");
    this.ui.onZoom = (dir) => this.rig.zoom(dir * 180);
    this.ui.onHome = () => this.resetHome();
    this.ui.onLabelClick = (kind, id) => this.handleLabel(kind, id);

    // CSS2D labels: allow clicks on tags only
    this.labels.domElement.style.pointerEvents = "none";
    this.labels.domElement.addEventListener("click", (e) => this.onLabelClick(e));
    this.viewport.addEventListener("click", this.onPick);

    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.onGlobalKey);
    this.onResize();
    this.raf = requestAnimationFrame(this.loop);
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("keydown", this.onGlobalKey);
    this.viewport.removeEventListener("click", this.onPick);
    this.input.dispose();
    this.audio.dispose();
    this.renderer.dispose();
  }

  private async enter(): Promise<void> {
    this.started = true;
    this.ui.startPlay();
    this.input.enabled = true;
    this.audio.setMuted(this.save.muted);
    await this.audio.start();
  }

  private resetHome(): void {
    orientOnSphere(this.player.group, this.spawn.clone(), tangentBasis(this.spawn).north);
    this.guiding.clear();
    this.walkTarget = null;
  }

  private handleLabel(kind: "landmark" | "npc", id: string): void {
    if (kind === "landmark") {
      const lm = landmarkById(id as LandmarkId);
      const fresh = unlockPostcard(this.save, lm.id);
      this.ui.refresh(this.save);
      // 没有站点的风景小屋：再点也只是重看明信片，不跳转
      if (fresh || !lm.url) this.ui.showCollect({ kind: "postcard", id: lm.id });
      else this.ui.onOpenSite(lm.url);
      return;
    }
    this.ui.openTalk(id as NpcId);
  }

  private onLabelClick(event: Event): void {
    if (!this.started || this.ui.blocking) return;
    const target = event.target as HTMLElement | null;
    const el = target?.closest?.("[data-kind][data-id]") as HTMLElement | null;
    if (!el?.dataset.kind || !el.dataset.id) return;
    event.stopPropagation();
    this.handleLabel(el.dataset.kind as "landmark" | "npc", el.dataset.id);
  }

  private onPick = (event: MouseEvent): void => {
    if (!this.started || this.ui.blocking) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2(x, y), this.camera);
    const hits = ray.intersectObjects([...this.landmarks.values(), ...this.npcs.values()], true);
    if (!hits[0]) return;
    let obj: THREE.Object3D | null = hits[0].object;
    while (obj && !obj.userData?.kind) obj = obj.parent;
    if (!obj?.userData?.kind) return;
    this.handleLabel(obj.userData.kind, obj.userData.id);
  };

  private tapToMove(): void {
    const tap = this.input.consumeTap();
    if (!tap || !this.started || this.ui.blocking || !this.planetMesh) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.ndc.set(
      ((tap.x - rect.left) / rect.width) * 2 - 1,
      -((tap.y - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObject(this.planetMesh, false);
    if (hits[0]) {
      this.walkTarget = hits[0].point.clone().normalize().multiplyScalar(PLANET_RADIUS);
    }
  }

  private onGlobalKey = (event: KeyboardEvent): void => {
    if (event.code === "Escape") {
      this.ui.closeModals();
      return;
    }
    if (!this.started) return;
    if (event.code === "KeyM") this.ui.toggleMute();
    if (event.code === "KeyH") this.resetHome();
    if (event.code === "KeyE" && this.nearbyNpc && !this.ui.blocking) {
      this.ui.openTalk(this.nearbyNpc);
    }
    if (event.code === "KeyF" && this.nearbyLandmark && !this.ui.blocking) {
      this.handleLabel("landmark", this.nearbyLandmark);
    }
  };

  private onResize = (): void => {
    const w = this.viewport.clientWidth;
    const h = this.viewport.clientHeight;
    this.camera.aspect = w / Math.max(h, 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.labels.setSize(w, h);
  };

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const t = this.clock.elapsedTime;
    // 系统要求减少动画时：云不转、粒子和精灵待机动作停掉、门页不环绕
    if (!this.reduceMotion) {
      this.clouds.rotation.y += dt * 0.02;
      this.ambientTick(t, dt);
      for (const g of this.npcs.values()) (g.userData.tick as ((t: number, dt: number) => void) | undefined)?.(t, dt);
      for (const g of this.landmarks.values()) (g.userData.tick as ((t: number, dt: number) => void) | undefined)?.(t, dt);
    }

    if (!this.started) {
      if (!this.reduceMotion) this.introYaw += dt * 0.1;
      this.camera.position.set(Math.cos(this.introYaw) * 38, 16, Math.sin(this.introYaw) * 38);
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(0, 0, 0);
      this.renderer.render(this.scene, this.camera);
      this.labels.render(this.scene, this.camera);
      return;
    }

    const orbit = this.input.consumeOrbit();
    this.rig.orbit(orbit.x, orbit.y);
    this.rig.zoom(this.input.consumeWheel());
    this.tapToMove();

    this.up.copy(this.player.position).normalize();
    this.fwd.copy(this.camera.getWorldDirection(this.fwd)).projectOnPlane(this.up).normalize();
    this.right.crossVectors(this.fwd, this.up).normalize();

    const stick = this.input.move;
    this.moveDir.set(0, 0, 0);
    if (!this.ui.blocking) {
      this.moveDir.addScaledVector(this.fwd, stick.y);
      this.moveDir.addScaledVector(this.right, stick.x);
    }
    const keyMoving = this.moveDir.lengthSq() > 1e-6;
    if (keyMoving) {
      this.walkTarget = null;
      this.moveDir.normalize();
      const next = moveOnSphere(this.player.position, this.moveDir, PLAYER_SPEED * dt, PLANET_RADIUS);
      orientOnSphere(this.player.group, next, this.moveDir);
    } else if (this.walkTarget && !this.ui.blocking) {
      const dist = surfaceDistance(this.player.position, this.walkTarget, PLANET_RADIUS);
      if (dist < 0.35) {
        this.walkTarget = null;
      } else {
        const next = greatCircleStep(this.player.position, this.walkTarget, PLAYER_SPEED * dt, PLANET_RADIUS);
        const dir = next.clone().sub(this.player.position);
        orientOnSphere(this.player.group, next, dir);
      }
    }
    this.player.moving = keyMoving || this.walkTarget !== null;

    this.updateGuides(dt);
    this.player.update(dt, t);
    this.probeProximity();
    this.rig.follow(this.player.position);
    this.renderer.render(this.scene, this.camera);
    this.labels.render(this.scene, this.camera);
    this.input.endFrame();
  };

  private updateGuides(dt: number): void {
    for (const id of [...this.guiding]) {
      const npc = this.npcs.get(id);
      const def = NPCS.find((n) => n.id === id);
      if (!npc || !def) {
        this.guiding.delete(id);
        continue;
      }
      const target = this.landmarks.get(def.guideTo)?.position;
      if (!target) continue;
      const dist = surfaceDistance(npc.position, target, PLANET_RADIUS);
      if (dist < 1.2) {
        this.guiding.delete(id);
        continue;
      }
      const next = greatCircleStep(npc.position, target, NPC_SPEED * dt, PLANET_RADIUS);
      const dir = next.clone().sub(npc.position);
      orientOnSphere(npc, next, dir);
    }
  }

  private probeProximity(): void {
    let bestNpc: NpcId | null = null;
    let bestNpcD = INTERACT_RADIUS;
    for (const def of NPCS) {
      const mesh = this.npcs.get(def.id);
      if (!mesh) continue;
      const d = surfaceDistance(this.player.position, mesh.position, PLANET_RADIUS);
      if (d < bestNpcD) {
        bestNpcD = d;
        bestNpc = def.id;
      }
    }
    this.nearbyNpc = bestNpc;

    let bestLm: LandmarkId | null = null;
    let bestLmD = LANDMARK_RADIUS;
    for (const def of LANDMARKS) {
      const mesh = this.landmarks.get(def.id);
      if (!mesh) continue;
      const d = surfaceDistance(this.player.position, mesh.position, PLANET_RADIUS);
      if (d < bestLmD) {
        bestLmD = d;
        bestLm = def.id;
      }
      // auto-unlock postcard when close
      if (d < LANDMARK_RADIUS * 0.85) {
        if (unlockPostcard(this.save, def.id)) {
          this.ui.refresh(this.save);
          this.ui.showCollect({ kind: "postcard", id: def.id });
        }
      }
    }
    this.nearbyLandmark = bestLm;

    if (bestNpc && unlockBadge(this.save, bestNpc)) {
      this.ui.refresh(this.save);
      this.ui.showCollect({ kind: "badge", id: bestNpc });
    }

    if (bestNpc) {
      this.ui.setHint(`${NPCS.find((n) => n.id === bestNpc)?.name} 在旁边，按 E 聊天`, bestNpc);
    } else if (bestLm) {
      const lm = landmarkById(bestLm);
      this.ui.setHint(lm.url ? `到 ${lm.name} 门口了，按 F 进去` : `这里是 ${lm.name}，只是风景`, null, bestLm);
    } else {
      this.ui.setHint(null, null, null);
    }

    const nearEl = (bestNpc ? this.npcs.get(bestNpc) : bestLm ? this.landmarks.get(bestLm) : null)?.userData
      .label as HTMLElement | undefined;
    if (nearEl !== this.nearLabel) {
      this.nearLabel?.classList.remove("near");
      nearEl?.classList.add("near");
      this.nearLabel = nearEl ?? null;
    }
  }
}
